import { useEffect, useState } from "react";
import { Bar, Column, Line, Pie } from "@ant-design/plots";
import { Card, Empty, Skeleton } from "antd";
import {
  BarChart3,
  LineChart,
  BarChartHorizontal,
  PieChart as LucidePieChart,
} from "lucide-react";

const LINE_CHART_COLOR = "#5D44FF";
const PIE_CHART_COLORS = [
  "#2F80ED",
  "#27AE60",
  "#9B51E0",
  "#F2994A",
  "#EB5757",
  "#14B8A6",
  "#1D4ED8",
  "#16A34A",
  "#8B5CF6",
  "#F59E0B",
];

const getChartConfig = (type, rawData, customConfig) => {
  const normalizedRawData = Array.isArray(rawData) ? rawData : [];
  const hasCategorySeries = normalizedRawData.some(
    (item) => item?.category !== undefined && item?.category !== null && item?.category !== "",
  );
  const commonConfig = {
    data: normalizedRawData.map((item) => ({
      ...item,
      label: item?.label?.toString?.(),
      value: Number(item?.value ?? 0),
      ...(hasCategorySeries
        ? { category: item?.category?.toString?.() || "Others" }
        : {}),
    })),
    legend: {
      position: "bottom",
    },
    tooltip: {
      showMarkers: true,
      shared: true,
    },
    color: () => {
      return "#5B8FF9";
    },
  };

  switch (type) {
    case "bar":
      return {
        ...commonConfig,
        isStack: hasCategorySeries,
        xField: "value",
        yField: "label",
        seriesField: hasCategorySeries ? "category" : undefined,
        isGroup: false,
        tooltip: {
          ...commonConfig.tooltip,
          formatter: (datum) => ({
            name: datum.category || datum.label,
            value: datum.value,
          }),
        },
        ...customConfig,
      };

    case "column":
      return {
        ...commonConfig,
        xField: "label",
        yField: "value",
        seriesField: hasCategorySeries ? "category" : undefined,
        isGroup: false,
        isStack: hasCategorySeries,
        tooltip: {
          ...commonConfig.tooltip,
          formatter: (datum) => ({
            name: datum.category || datum.label,
            value: datum.value,
          }),
        },
        ...customConfig,
      };

    default:
      return {};
  }
};

const ChartRenderer = ({
  type = "bar",
  data = [],
  title,
  description,
  customConfig,
  loading = false,
}) => {
  const [activeType, setActiveType] = useState("bar");
  const config = getChartConfig(activeType, data, customConfig);

  useEffect(() => {
    setActiveType(type);
  }, [type]);

  const getPieData = () => {
    const rawData = Array.isArray(data) ? data : [];
    const hasCategory = rawData.some(
      (item) => item?.category !== undefined && item?.category !== null && item?.category !== "",
    );

    let aggregated = [];
    if (hasCategory) {
      const grouped = rawData.reduce((acc, curr) => {
        const cat = curr?.category?.toString?.() || "Others";
        if (!acc[cat]) {
          acc[cat] = { label: cat, category: cat, value: 0 };
        }
        acc[cat].value += Number(curr?.value ?? 0);
        return acc;
      }, {});
      aggregated = Object.values(grouped);
    } else {
      const grouped = rawData.reduce((acc, curr) => {
        const lbl = curr?.label?.toString?.() || "Others";
        if (!acc[lbl]) {
          acc[lbl] = { label: lbl, value: 0 };
        }
        acc[lbl].value += Number(curr?.value ?? 0);
        return acc;
      }, {});
      aggregated = Object.values(grouped);
    }
    return aggregated;
  };

  const pieChartData = {
    data: getPieData(),
    angleField: "value",
    colorField: "label",
    color: customConfig?.color || PIE_CHART_COLORS,
    radius: 0.8,
    innerRadius: 0.7,
    label: {
      type: "outer",
      content: "{name} ({percentage})",
      style: {
        fontSize: 12,
        fontWeight: 500,
      },
    },
    interactions: [{ type: "element-active" }],
    statistic: {
      title: {
        style: {
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: "16px",
          color: "#64748B",
          lineHeight: "24px",
          textAlign: "center",
        },
        content: "Total",
      },
      content: {
        style: {
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: "28px",
          fontWeight: 700,
          color: "#1E293B",
          lineHeight: "36px",
          textAlign: "center",
        },
        formatter: () => {
          const total = (Array.isArray(data) ? data : []).reduce(
            (acc, item) => acc + Number(item?.value ?? 0),
            0,
          );
          return total.toLocaleString();
        },
      },
    },
    pieStyle: {
      stroke: "#fff",
      lineWidth: 2,
    },
    animation: {
      appear: {
        animation: "ant-motion",
        duration: 1000,
      },
    },
    tooltip: {
      formatter: (datum) => ({
        name: datum.label,
        value: datum.value,
      }),
    },
    legend: {
      position: "bottom",
    },
    onReady: (val) => customConfig?.onReady?.(val),
  };


  const baseLineChart = {
    data: (Array.isArray(data) ? data : []).map((item) => ({
      ...item,
      label: item?.label?.toString?.(),
      value: Number(item?.value ?? 0),
    })),
    xField: "label",
    yField: "value",
    seriesField: null,
    smooth: true,
    animation: false,
    lineStyle: {
      lineWidth: 3,
      lineCap: "round",
      lineJoin: "round",
      shadowColor: "rgba(93, 68, 255, 0.15)",
      shadowBlur: 10,
    },
    point: {
      size: 0,
      shape: "circle",
      style: {
        fill: "#ffffff",
        stroke: LINE_CHART_COLOR,
        lineWidth: 2,
      },
    },
    area: {
      style: {
        fill: "l(270) 0:rgba(93, 68, 255, 0.18) 1:rgba(93, 68, 255, 0.02)",
      },
    },
    state: {
      active: {
        style: {
          lineWidth: 3,
        },
      },
    },
    tooltip: {
      showMarkers: true,
      shared: true,
      formatter: (datum) => ({
        name: datum.label,
        value: datum.value,
      }),
    },
    interactions: [{ type: "marker-active" }],
    onReady: (val) => customConfig?.onReady?.(val),
    color: () => {
      return LINE_CHART_COLOR;
    },
  };

  const lineChart = {
    ...baseLineChart,
    ...customConfig,
    point: {
      ...baseLineChart.point,
      ...customConfig?.point,
      style: {
        ...baseLineChart.point?.style,
        ...customConfig?.point?.style,
      },
    },
    area: {
      ...baseLineChart.area,
      ...customConfig?.area,
      style: {
        ...baseLineChart.area?.style,
        ...customConfig?.area?.style,
      },
    },
    lineStyle: {
      ...baseLineChart.lineStyle,
      ...customConfig?.lineStyle,
    },
    tooltip: {
      ...baseLineChart.tooltip,
      ...customConfig?.tooltip,
    },
  };

  const ChartComponent = {
    bar: Bar,
    column: Column,
  }[activeType];

  return (
    <Card className="tw-border tw-border-border-secondary h-100">
      <div className="d-flex align-items-start justify-content-between gap-10">
        <div className="tw-text-base tw-font-semibold">{title}</div>
        <div className="d-flex align-items-center gap-10 tw-bg-secondary-color tw-rounded-md tw-p-1">
          <div
            className={`${activeType === "bar" ? "tw-bg-white tw-text-theme" : "tw-text-sub-text"} tw-p-1 tw-cursor-pointer tw-rounded-sm tw-w-30 tw-h-30 tw-flex tw-items-center tw-justify-center tw-text-lg`}
            onClick={() => setActiveType("bar")}
          >
            <BarChartHorizontal className="tw-w-4 tw-h-4" />
          </div>
          <div
            className={`${activeType === "line" ? "tw-bg-white tw-text-theme" : "tw-text-sub-text"} tw-p-1 tw-cursor-pointer tw-rounded-sm tw-w-30 tw-h-30 tw-flex tw-items-center tw-justify-center tw-text-lg`}
            onClick={() => setActiveType("line")}
          >
            <LineChart className="tw-w-4 tw-h-4" />
          </div>
          <div
            className={`${activeType === "column" ? "tw-bg-white tw-text-theme" : "tw-text-sub-text"} tw-p-1 tw-cursor-pointer tw-rounded-sm tw-w-30 tw-h-30 tw-flex tw-items-center tw-justify-center tw-text-lg`}
            onClick={() => setActiveType("column")}
          >
            <BarChart3 className="tw-w-4 tw-h-4" />
          </div>
          <div
            className={`${activeType === "pie" ? "tw-bg-white tw-text-theme" : "tw-text-sub-text"} tw-p-1 tw-cursor-pointer tw-rounded-sm tw-w-30 tw-h-30 tw-flex tw-items-center tw-justify-center tw-text-lg`}
            onClick={() => setActiveType("pie")}
          >
            <LucidePieChart className="tw-w-4 tw-h-4" />
          </div>
        </div>
      </div>
      {description && <div className="text-gray-800">{description}</div>}
      {loading ? (
        <div className="mt30">
          <Skeleton active paragraph={{ rows: 8 }} title={false} />
        </div>
      ) : !!data?.length ? (
        <div className="mt30">
          {activeType === "pie" ? (
            <Pie {...pieChartData} style={{ height: 320 }} />
          ) : activeType === "line" ? (
            <Line {...lineChart} style={{ height: 320 }} />
          ) : (
            <ChartComponent {...config} style={{ height: 320 }} />
          )}
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: 320 }}
        >
          <Empty
            description="No data available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}
    </Card>
  );
};

export default ChartRenderer;
